import React, { createContext, useState, useContext, useEffect, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import Cookies from 'js-cookie';

// Define the context type
interface LanguageContextType {
  language: string;
  changeLanguage: (lang: string) => Promise<void>;
  isLoading: boolean;
}

// Create the context with default values
const LanguageContext = createContext<LanguageContextType>({
  language: 'en',
  changeLanguage: async () => {},
  isLoading: false,
});

// Custom hook to use the language context
export const useLanguage = () => useContext(LanguageContext);

interface LanguageProviderProps {
  children: ReactNode;
}

// Cookie name for storing language preference - must match i18next config
const LANGUAGE_COOKIE_NAME = 'i18next';
// Cookie expiration in days
const COOKIE_EXPIRATION_DAYS = 365;

// Language provider component
export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
  const { i18n } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [languageState, setLanguageState] = useState(i18n.language);

  // Initialize language from cookie, localStorage, or browser
  useEffect(() => {
    // Check for language in this order: cookie, localStorage, browser
    const cookieLanguage = Cookies.get(LANGUAGE_COOKIE_NAME);
    const localStorageLanguage = localStorage.getItem('i18nextLng');
    const browserLanguage = navigator.language.split('-')[0]; // Get base language code (en, sv)

    console.log('Language detection:', {
      cookieLanguage,
      localStorageLanguage,
      browserLanguage,
      currentI18nLanguage: i18n.language
    });

    // Use the first available language preference
    const detectedLanguage = cookieLanguage || localStorageLanguage || browserLanguage;

    // Only change if it's a supported language and different from current
    if (detectedLanguage && ['en', 'sv'].includes(detectedLanguage) && detectedLanguage !== i18n.language) {
      console.log(`Setting detected language: ${detectedLanguage}`);
      i18n.changeLanguage(detectedLanguage);
      setLanguageState(detectedLanguage);
    }
  }, [i18n]);

  // Function to change language
  const changeLanguage = async (lang: string) => {
    if (lang === i18n.language) return; // Don't do anything if language is already set

    setIsLoading(true);
    try {
      console.log(`Changing language to: ${lang}`);

      // Update language in i18next
      await i18n.changeLanguage(lang);

      // Store in localStorage (primary storage)
      localStorage.setItem('i18nextLng', lang);

      // Also save in cookie for cross-page consistency
      Cookies.set(LANGUAGE_COOKIE_NAME, lang, {
        expires: COOKIE_EXPIRATION_DAYS,
        sameSite: 'lax',
        secure: window.location.protocol === 'https:',
        path: '/'
      });

      // Force a context update to ensure all components re-render
      setLanguageState(lang);

      console.log(`Language changed to: ${i18n.language}`);
    } catch (error) {
      console.error('Failed to change language:', error);
      // Revert to previous language in case of error
      setLanguageState(i18n.language);
    } finally {
      setIsLoading(false);
    }
  };

  // Context value
  const contextValue: LanguageContextType = {
    language: languageState,
    changeLanguage,
    isLoading,
  };

  return (
    <LanguageContext.Provider value={contextValue}>
      {children}
    </LanguageContext.Provider>
  );
};

export default LanguageProvider;
